import React, { useState, ChangeEvent, useId } from 'react';
import { calculateEndDate, toDateString } from '@/utilities';

export type DateRangeSelectorProps = {
  startDate?: Date;
  endDate?: Date;
  startLabel?: string;
  endLabel?: string;
  direction?: "horizontal" |  "vertical" | string;
  onDateChange?: (startDate: Date, endDate: Date, dateError: string) => void;
  hasMinDate?: boolean;
  allowSameDate?: boolean;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  direction,
  startLabel = "Start Date",
  endLabel = "End Date",
  startDate,
  endDate,
  onDateChange,
  hasMinDate = true,
  allowSameDate = false
}) => {
  const uniqueId = useId();
  const [start, setStartDate] = useState<string>(startDate ? toDateString(startDate) : "");
  const [end, setEndDate] = useState<string>(endDate ? toDateString(endDate) : "");
  const [error, setError] = useState<string>("");

  const handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedStartDateStr = e.target.value;
    const selectedStartDate = new Date(selectedStartDateStr);
    setStartDate(selectedStartDateStr);
    if (end && selectedStartDate >= new Date(end)) {
      const newEndStr = toDateString(calculateEndDate(1, selectedStartDate));
      setEndDate(newEndStr);
      validateDates(selectedStartDateStr, newEndStr);
    } else {
      validateDates(selectedStartDateStr, end);
    }
  };

  const handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedEndDateStr = e.target.value;
    setEndDate(selectedEndDateStr);
    validateDates(start, selectedEndDateStr);
  };

  const validateDates = (start: string, end: string) => {
    let errorStr = "";
    const startObj = new Date(start + ` ${new Date().getHours()}:00`);
    const endObj = new Date(end + ` ${new Date().getHours()}:00`);
    if (!start || !end) {
      errorStr = "Please select a start and end date.";
    } else if (end && (allowSameDate ? startObj > endObj : startObj >= endObj)) {
      errorStr = allowSameDate 
        ? "End date must be the same as or after the start date."
        : "End date must be at least one day after the start date.";
    }
    setError(errorStr);
    if (onDateChange) onDateChange(startObj, endObj, errorStr);
  };

  return (
  <>
    <div className={`d-flex flex-${direction === 'vertical' ? 'column' : 'row'} gap-2`}>
      <div className='form-block col'>
        <label htmlFor={`startDate-${uniqueId}`} className="form-label">{startLabel}*</label>
        <input
          type="date"
          id={`startDate-${uniqueId}`}
          className="form-control"
          value={start}
          min={hasMinDate && start ? start : undefined}
          onChange={handleStartDateChange}
          required
        />
      </div>

      <div className="form-block col">
        <label htmlFor={`endDate-${uniqueId}`} className="form-label">{endLabel}*</label>
        <input
          type="date"
          id={`endDate-${uniqueId}`}
          className="form-control"
          value={end}
          min={start ? (allowSameDate ? start : toDateString(calculateEndDate(2, new Date(start)))) : undefined}
          onChange={handleEndDateChange}
          required
        />
      </div>
    </div>
    {error && <div className="d-block text-danger mt-2">{error}</div>}
  </>
  );
};

export default DateRangeSelector;
